import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/apiClient";
import {
  PageHead, Card, Pill, FilterBar, TextInput, Select, Loading, Err, tableStyles, EmptyState,
} from "./_shared";

const ROLE_OPTIONS = ["super_admin", "credit_admin", "support_admin", "workspace_owner", "workspace_member"];

export default function Users() {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");

  useEffect(() => {
    const url = new URLSearchParams();
    if (q) url.set("q", q);
    if (role) url.set("role", role);
    api.get("/api/admin/users" + (url.toString() ? "?" + url.toString() : ""))
      .then((d) => setItems(d.items || [])).catch((e) => setErr(e.message));
  }, [q, role]);

  const summary = useMemo(() => {
    if (!items) return null;
    return {
      total: items.length,
      admins: items.filter((u) => (u.roles || []).includes("super_admin")).length,
    };
  }, [items]);

  if (err) return <Err msg={err} />;

  return (
    <div data-testid="admin-users">
      <PageHead title="Platform users" sub={summary ? `${summary.total} users · ${summary.admins} super admins` : "Loading…"} />

      <FilterBar>
        <TextInput value={q} onChange={setQ} placeholder="Search by email or name…" data-testid="users-search" />
        <Select value={role} onChange={setRole} placeholder="All roles" options={ROLE_OPTIONS} data-testid="users-filter-role" />
      </FilterBar>

      {!items ? <Loading /> : items.length === 0 ? (
        <EmptyState title="No users match these filters" />
      ) : (
        <Card pad={0} style={{ overflow: "hidden" }}>
          <table style={tableStyles.table}>
            <thead>
              <tr>
                <th style={tableStyles.th}>User</th>
                <th style={tableStyles.th}>Roles</th>
                <th style={{ ...tableStyles.th, textAlign: "right" }}>Workspaces</th>
                <th style={tableStyles.th}>Last active</th>
                <th style={tableStyles.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} style={{ borderTop: "1px solid var(--pl-border)" }}>
                  <td style={tableStyles.td}>
                    <div style={{ fontWeight: 700 }}>{u.name || "—"}</div>
                    <div style={{ fontSize: 11, color: "var(--pl-text-muted)" }}>{u.email}</div>
                  </td>
                  <td style={tableStyles.td}>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {(u.roles || []).map((r) => <Pill key={r} tone={r === "super_admin" ? "lime" : "muted"}>{r}</Pill>)}
                    </div>
                  </td>
                  <td style={{ ...tableStyles.td, textAlign: "right", fontFamily: "JetBrains Mono, monospace" }}>{u.workspace_count ?? 0}</td>
                  <td style={{ ...tableStyles.td, fontSize: 11 }}>{(u.last_login_at || u.updated_at || u.created_at || "").slice(0, 19)}</td>
                  <td style={tableStyles.td}>
                    <Pill tone={u.status === "active" || !u.status ? "success" : "warn"}>{u.status || "active"}</Pill>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
